import React, { useEffect, useState } from "react";
import styled from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import labels from "../../../helpers/labels.json";
import { CircularProgress, Modal } from "@material-ui/core";
import SearchBar from "../../common/SearchBar";
import CaseService from "../../../services/CaseService";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check"; // For selected state
import useDebounce from "../../../hooks/useDebounce";
import queryString from "query-string";
import _ from "lodash";

export default function AssignCaseManager({
  state,
  setState,
  cmList,
  setCmList,
  coManagers,
  setCoManagers,
  selectingFor,
  setSelectingFor,
}) {
  const { enqueueSnackbar } = useSnackbar();

  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [caseManagers, setCaseManagers] = useState([]);
  const debounceValue = useDebounce(inputText, 500);

  useEffect(() => {
    if (state) {
      getCMList(debounceValue);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, debounceValue]); 

  async function getCMList(query) {
    try {
      setLoading(true);
      let params = {
        page: 1,
        perPage: 1000,
        role: "caseManager",
      };
      if (query) {
        params.query = query;
      }
      let stringParams = !_.isEmpty(params)
        ? `?${queryString.stringify(params)}`
        : "";

      const response = await CaseService.getCMlist(stringParams);
      if (response?.data) {
        setCaseManagers(response.data);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }

  const handleSelect = (cm) => {
    if (selectingFor === "primary") {
      setCmList(cm);
      setState(false);
    } else {
      setCoManagers((prev) => {
        const exists = prev.some((c) => c.id === cm.id);
        if (exists) {
          return prev.filter((c) => c.id !== cm.id);
        } else {
          return [...prev, cm];
        }
      });
    }
  };

  const isSelected = (cm) => {
    if (selectingFor === "primary") {
      return cmList?.id === cm.id;
    } else {
      return coManagers.some((c) => c.id === cm.id);
    }
  };

  return (
    <Modal open={state} onClose={() => setState(false)} disableRestoreFocus>
      <DrawerContainer role="presentation">
        <HeadingContainer>
          <Heading>
            {selectingFor === "primary"
              ? "Select Primary Case Manager"
              : "Select Case Managers"}
          </Heading>
          <CloseModal
            onClick={() => setState(false)}
            src={require("../../../assets/images/closeModal.svg")}
          />
        </HeadingContainer>

        <ContentContainer>
          <SearchBar
            name="caseManagerSearch"
            placeHolderText={labels["search_case_manager"]}
            backgroundColor="#f9f9f9"
            {...{ inputText, setInputText }}
            width="100%"
            autoFocus
          />

          <ListContainer>
            {loading ? (
              <Loader>
                <CircularProgress />
              </Loader>
            ) : caseManagers.length > 0 ? (
              caseManagers.map((cm) => {
                const selected = isSelected(cm);
                return (
                  <CMRow
                    key={cm.id}
                    selected={selected}
                    onClick={() => handleSelect(cm)}
                  >
                    <CMInfo>
                      <div>{cm.name}</div>
                      <div className="email">({cm.email})</div>
                    </CMInfo>
                    <IconContainer selected={selected}>
                      {selected ? (
                        <CheckIcon style={{ fontSize: 18, color: "white" }} />
                      ) : (
                        <AddIcon className="add_icon" />
                      )}
                    </IconContainer>
                  </CMRow>
                );
              })
            ) : (
              <NoResult>No case managers found.</NoResult>
            )}
          </ListContainer>

          {selectingFor === "co" && coManagers.length > 0 && (
            <SelectedSummary>
              <strong>{coManagers.length}</strong> co-manager(s) selected
            </SelectedSummary>
          )}
        </ContentContainer>
      </DrawerContainer>
    </Modal>
  );
}

const DrawerContainer = styled.div`
  max-width: 546px;
  width: 100%;
  max-height: 80vh;
  background-color: white;
  outline: none;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 8px;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
`;

const HeadingContainer = styled.div`
  padding: 30px 30px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  flex-shrink: 0;
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  cursor: pointer;
  position: absolute;
  right: 30px;
`;

const Heading = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
  text-align: center;
`;

const ContentContainer = styled.div`
  padding: 0 40px 30px;
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const ListContainer = styled.div`
  margin-top: 20px;
  max-height: 400px;
  overflow-y: auto;
  flex: 1;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const NoResult = styled.div`
  text-align: center;
  padding: 40px;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 16px;
`;

const CMRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9f9f9;
  }

  ${({ selected }) =>
    selected &&
    `
    background: rgba(0, 131, 140, 0.08);
    & .email {
      color: ${COLORS.BTN_GREEN};
    }
  `}
`;

const CMInfo = styled.div`
  flex: 1;
  & > div:first-child {
    font-size: 16px;
    font-weight: 600;
    color: ${COLORS.COLOR_DARK};
    font-family: ${theme.fonts.primaryFontSemiBold};
  }
  & .email {
    font-size: 14px;
    color: #949ab0;
    font-family: ${theme.fonts.primaryFontSemiBold};
    margin-top: 4px;
  }
`;

const IconContainer = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ selected }) =>
    selected ? COLORS.BTN_GREEN : "rgba(0, 131, 140, 0.13)"};
  transition: all 0.2s;

  & .add_icon {
    font-size: 16px;
    color: ${COLORS.BTN_GREEN};
  }
`;

const SelectedSummary = styled.div`
  margin-top: 15px;
  text-align: center;
  padding: 10px;
  background: rgba(0, 131, 140, 0.1);
  border-radius: 8px;
  color: ${COLORS.BTN_GREEN};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
`;
